import { Injectable } from '@angular/core';

import { MsalService } from '@azure/msal-angular';
import { AuthenticationService } from './authentication.service';
import { Observable, of } from 'rxjs';
import { switchMap, catchError, map } from 'rxjs/operators';
import { AuthenticationResult } from '@azure/msal-browser';

/**
 * AuthGuard — protects all routes that require a valid Azure AD idToken
 * and a valid Apigee access token.
 *
 * Strategy (no full-page navigation away from the current route):
 * 1. Try acquireTokenSilent — works when the Azure AD SSO session is still alive
 *    (most common case for a returning user with a stale MMAPS session).
 * 2. If silent fails, fall back to loginPopup (authService.signIn()) — opens a
 *    small popup window, the main page/URL never changes.
 * 3. On a fresh Azure AD token, call authService.login() to exchange it for a
 *    new Apigee token and write everything to session/localStorage.
 * 4. Return true so Angular continues the original navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(
    private msalService: MsalService,
    private authService: AuthenticationService
  ) {}

  canActivate(): Observable<boolean> {
    // Short-circuit: if Apigee token exists and isn't near expiry, skip refresh.
    // IMPORTANT: also verify the Azure AD idToken is not expired. The Apigee
    // token and the idToken have independent lifetimes. The Apigee token can
    // still be valid while the idToken stored in userInfo has already expired.
    // Every API call sends userInfo.idToken to the Lambda Authorizer, which
    // strictly validates the exp claim — a stale idToken always produces a
    // "Signature has expired" → Deny → 403, regardless of Apigee token state.
    try {
      const fullTokenPayload = JSON.parse(sessionStorage.getItem('fullTokenPayload'));
      if (fullTokenPayload) {
        const issuedAt = fullTokenPayload.issued_at;
        const expiresInMs = Number(fullTokenPayload.expires_in) * 1000;
        const expiryTime = Number(issuedAt) + expiresInMs;
        const remainingMs = expiryTime - Date.now();
        const safetyWindow = 10 * 60 * 1000; // 10 minutes

        // Check whether the Azure AD idToken is still valid before short-circuiting.
        // If it is expired or within the same safety window, fall through to
        // performAzureExchange() so a fresh idToken is obtained via forceRefresh.
        let idTokenValid = false;
        try {
          const userInfo = JSON.parse(sessionStorage.getItem('userInfo'));
          const exp = userInfo && userInfo.idTokenClaims && userInfo.idTokenClaims.exp;
          if (exp) {
            idTokenValid = exp * 1000 - Date.now() > safetyWindow;
          }
        } catch (e) {
          /* fall through */
        }

        if (remainingMs > safetyWindow && idTokenValid) {
          console.log('[AuthGuard] Apigee token and idToken both valid, skipping refresh.');
          return of(true);
        }

        // Do NOT attempt an Apigee-only refresh here. refreshAccessToken() only
        // updates the Apigee token — it never refreshes userInfo.idToken. Every
        // API call sends userInfo.idToken to the Lambda Authorizer, so an Apigee
        // refresh that leaves an expired idToken in place still produces a
        // "Signature has expired" → 403 on every subsequent API call.
        // Always fall through to performAzureExchange() which uses forceRefresh:true
        // to get both a fresh Azure AD idToken AND a new Apigee token atomically.
      }
    } catch (e) {
      // If parsing storage fails, proceed with normal exchange flow
    }
    return this.performAzureExchange();
  }

  private performAzureExchange(): Observable<boolean> {
    // Reuse whichever account MSAL already knows about
    const accounts = this.msalService.instance.getAllAccounts();
    const account =
      this.msalService.instance.getActiveAccount() || (accounts.length > 0 ? accounts[0] : null);

    // If MSAL has no account at all, go straight to popup
    if (!account) {
      return this.signInThenExchange();
    }

    // forceRefresh: true forces MSAL to use its refresh token to obtain a
    // genuinely new idToken from Azure AD rather than returning the cached
    // idToken, which can be hours old. MSAL caches the idToken independently
    // of the access token — even with 'openid' in scopes, acquireTokenSilent
    // without forceRefresh can return a cached idToken that has already passed
    // its exp claim, causing the Lambda Authorizer to reject it with
    // "Signature has expired" → 403.
    return this.msalService
      .acquireTokenSilent({ scopes: ['openid', 'user.read'], account, forceRefresh: true })
      .pipe(
        switchMap((result: AuthenticationResult) => this.storeAndExchange(result)),
        catchError((silentErr) => {
          console.warn(
            '[AuthGuard] Silent token acquisition failed, falling back to popup.',
            silentErr
          );
          return this.signInThenExchange();
        })
      );
  }

  /** Trigger MSAL loginPopup then exchange the resulting token for an Apigee token. */
  private signInThenExchange(): Observable<boolean> {
    return this.authService.signIn().pipe(
      switchMap((result: AuthenticationResult) => this.storeAndExchange(result)),
      catchError((err) => {
        console.error('[AuthGuard] Login popup failed or was cancelled.', err);
        return of(false);
      })
    );
  }

  /**
   * Write a fresh Azure AD AuthenticationResult to storage (exactly as LoginComponent
   * and listenForMsalTokenRenewal already do), then exchange it for a new Apigee token
   * via the existing authService.login() call.
   */
  private storeAndExchange(result: AuthenticationResult): Observable<boolean> {
    if (!result || !result.idToken) {
      return of(false);
    }

    // Verify the idToken is actually non-expired before sending it to Apigee.
    // acquireTokenSilent can return a cached idToken that is already past its
    // exp claim, which Apigee's jwt-bearer policy will reject with 401.
    const exp = result.idTokenClaims && (result.idTokenClaims as any).exp;
    if (exp && exp * 1000 <= Date.now()) {
      console.warn(
        '[AuthGuard] acquireTokenSilent returned an already-expired idToken (exp:',
        new Date(exp * 1000).toISOString(),
        ') — falling back to popup.'
      );
      return this.signInThenExchange();
    }

    console.log(
      '[AuthGuard] idToken valid until:',
      exp ? new Date(exp * 1000).toISOString() : 'unknown'
    );

    // Persist fresh Azure AD tokens — identical to what LoginComponent.ngOnInit does
    const userInfo = JSON.stringify(result);
    sessionStorage.setItem('userInfo', userInfo);
    localStorage.setItem('userInfo', userInfo);

    // Exchange for a new Apigee token — authService.login() writes to
    // currentUser + fullTokenPayload in session/localStorage
    return this.authService.login().pipe(
      map(() => {
        console.log('[AuthGuard] Tokens refreshed successfully.');
        return true;
      }),
      catchError((err) => {
        console.error('[AuthGuard] Apigee token exchange failed.', err);
        return of(false);
      })
    );
  }
}
